import { ChangeDetectionStrategy, Component, input, TemplateRef, viewChild } from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';

/**
 * HxSegment Component
 *
 * Represents a single segment (tab) within a segmented control. Contains both the segment button label and its
 * associated content.
 *
 * @example
 *   ```html
 *   <hx-segmented-control>
 *     <hx-segment label="Overview" [key]="'overview'">
 *       <h2>Overview Content</h2>
 *       <p>This is the overview panel.</p>
 *     </hx-segment>
 *     <hx-segment label="Details" [key]="'details'">
 *       <h2>Details Content</h2>
 *       <p>This is the details panel.</p>
 *     </hx-segment>
 *   </hx-segmented-control>
 *   ```;
 */
@Component({
  selector: 'hx-segment',
  standalone: true,
  template: '<ng-template><ng-content /></ng-template>',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxSegmentComponent extends HxBaseControl {
  /** Display text for the segment button. Automatically truncated to 50 characters if longer. */
  readonly label = input.required<string>();

  /**
   * Unique identifier for the segment. If not provided, generated from label (lowercase, hyphenated).
   *
   * @default Generated from label
   */
  readonly key = input<string>();

  /** Icon name for hx-icon component (optional). */
  readonly icon = input<string>();

  /** Optional numeric badge shown after the segment label. */
  readonly badgeCount = input<number | null>();

  /** Reference to the content template. Used internally by the parent segmented control. */
  readonly contentTemplate = viewChild.required(TemplateRef);

  /** Gets the effective key for this segment. If no key is provided, generates one from the label. */
  getKey(): string {
    const explicitKey = this.key();
    if (explicitKey) {
      return explicitKey;
    }
    // Generate key from label: lowercase, replace spaces with hyphens, remove special chars
    return this.label()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }
}
